/**
 * act-as-waap.ts — React Query hooks for WAAP surfaces via act-as.
 *
 * Covers the anchor-domain resolution, dashboard charts, the org-scoped
 * website list (GET /waap/domains/:anchorId/websites), and log-archive
 * configs/buckets for the B1 tabs (Overview, Websites, Logs).
 *
 * All calls use actAsClient so X-Act-As-Org is injected automatically from
 * the active session. WAAP responses are NOT enveloped — no .data unwrap
 * needed; guard for null on 400/404 instead.
 *
 * Only usable while getActAsSession() returns a non-null value. Hooks are
 * `enabled`-gated but callers must also guard the render path.
 */

import { useQuery } from '@tanstack/react-query';
import { actAsClient } from '@/api/act-as-client';

/* ── Types ──────────────────────────────────────────────────────────────── */

/** Anchor domain used to resolve the Conversant customer session. One per
 *  WAAP service — fetched from /services/:serviceId/waap/domains?per_page=1. */
export interface ActAsWAAPAnchorDomain {
  id: string;
  domain_name: string;
  status?: string;
  uid?: string;
}

/** One WAAP website row on the PUBLIC Open-API (vendor `publicWebsiteDTO`).
 *  Flatter than the private CDN-domain shape — no protection / cert metadata. */
export interface ActAsWAAPWebsite {
  /** Vendor numeric website id — the :websiteId path segment. */
  id: number;
  service_id: number;
  domain: string;
  origins: string[];
  http2: boolean;
  redirect_http_to_https: boolean;
  headers: Record<string, string>;
  alternate_domain_names: string[];
  created: string;
  updated: string;
}

/** WAAP website basic-info (read-only): domain + CNAME + delivery config. */
export interface ActAsWAAPWebsiteInfo {
  domain: string;
  cname: string;
  origins: ActAsWAAPOriginEntry[];
  http2: boolean;
  redirect_http_to_https: boolean;
}

/** One origin server entry (eagle_waf OriginEntryDTO). */
export interface ActAsWAAPOriginEntry {
  protocol: string;
  origin: string;
  weight: number;
}

/* ── Dashboard chart types (mirrors Partner dashboard-charts.ts) ─────────── */

export type WaapChartFormat = '.3s' | 'SMART_NUMBER' | ',d' | string;

export interface WaapPieSlice { name: string; value: number; }
export interface WaapTableColumn { key: string; label: string; kind: 'text' | 'number' | 'percent'; }
export type WaapTableRow = Record<string, string | number | null>;
export interface WaapTimeseriesSeries { key: string; label: string; }

interface WaapChartBase {
  key: string;
  title: string;
  /** Non-empty string when this individual widget failed server-side. */
  error: string;
}

export interface WaapNumberChart extends WaapChartBase {
  kind: 'number';
  value: number | null;
  unit: string;
  format: WaapChartFormat;
}

export interface WaapPieChart extends WaapChartBase {
  kind: 'pie';
  slices?: WaapPieSlice[];
}

export interface WaapTableChart extends WaapChartBase {
  kind: 'table';
  columns?: WaapTableColumn[];
  rows?: WaapTableRow[];
}

export interface WaapTimeseriesChart extends WaapChartBase {
  kind: 'timeseries';
  unit: string;
  format: WaapChartFormat;
  xKey: string;
  series?: WaapTimeseriesSeries[];
  data?: Record<string, unknown>[];
}

export type WaapChart =
  | WaapNumberChart
  | WaapPieChart
  | WaapTableChart
  | WaapTimeseriesChart;

export interface WaapChartsResponse { charts: WaapChart[]; }

/* ── Log-archive types ───────────────────────────────────────────────────── */

export interface ActAsWAAPOSSBucket {
  name: string;
  creation_date: number;
}

/** One log-archiving config row. Live accounts may have zero configs; field
 *  names are not confirmed from the vendor yet — unknown keys pass through. */
export interface ActAsWAAPLogArchiveConfig {
  id?: number | string;
  [key: string]: unknown;
}

/* ── Query key factory ───────────────────────────────────────────────────── */

export const actAsWAAPKeys = {
  all: ['act-as', 'waap'] as const,
  /** Anchor domain for a service (one-per-service resolution). */
  anchor: (serviceId: string) => ['act-as', 'waap', 'anchor', serviceId] as const,
  /** Aspect under an anchor domain (most config endpoints). */
  aspect: (anchorId: string, area: string) => ['act-as', 'waap', 'domain', anchorId, area] as const,
  /** Website list under an anchor domain. */
  websites: (anchorId: string) => ['act-as', 'waap', 'domain', anchorId, 'websites'] as const,
  /** Bot strategies (service-scoped). */
  botStrategies: (serviceId: string) => ['act-as', 'waap', 'service', serviceId, 'bot-strategies'] as const,
  /** Emergency config (service-scoped). */
  emergency: (serviceId: string) => ['act-as', 'waap', 'service', serviceId, 'emergency'] as const,
  /** Custom rules (service-scoped). */
  customRules: (serviceId: string) => ['act-as', 'waap', 'service', serviceId, 'custom-rules'] as const,
};

/* ── Anchor domain resolution ────────────────────────────────────────────── */

/**
 * Resolve the anchor domain ID for a WAAP service.
 *
 * GET /api/v3/services/:serviceId/waap/domains?per_page=1 — takes the first
 * domain UUID and holds it in cache for 5 min. This ID is passed to all
 * account-level WAAP endpoints to resolve the Conversant customer session.
 *
 * Returns undefined while loading; null when the service has no websites yet.
 */
export function useActAsWAAPAnchorDomain(serviceId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.anchor(serviceId ?? ''),
    enabled: Boolean(serviceId),
    staleTime: 5 * 60_000, // 5 min — anchor domain rarely changes
    queryFn: async (): Promise<ActAsWAAPAnchorDomain | null> => {
      const res = await actAsClient.get<unknown>(
        `/services/${serviceId}/waap/domains?per_page=1`,
      );
      const body = res.data as Record<string, unknown>;
      // Endpoint returns { results: [...] } or bare array.
      const items: ActAsWAAPAnchorDomain[] = Array.isArray(body)
        ? (body as ActAsWAAPAnchorDomain[])
        : Array.isArray(body.results)
          ? (body.results as ActAsWAAPAnchorDomain[])
          : Array.isArray(body.data)
            ? (body.data as ActAsWAAPAnchorDomain[])
            : [];
      return items[0] ?? null;
    },
  });
}

/* ── Dashboard charts ────────────────────────────────────────────────────── */

/**
 * GET /waap/domains/:anchorId/dashboard/charts — 30-day BI dashboard widgets.
 *
 * Returns null when the dashboard is not configured server-side (400/404/501)
 * so the tab can show a "not configured" state without crashing.
 */
export function useActAsWAAPDashboardCharts(
  anchorId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'dashboard/charts'),
    enabled: Boolean(anchorId) && enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await actAsClient.get<WaapChartsResponse>(
          `/waap/domains/${anchorId}/dashboard/charts?tz=${encodeURIComponent(tz)}`,
        );
        return res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404 || status === 501) return null;
        throw e;
      }
    },
  });
}

/* ── Websites list ───────────────────────────────────────────────────────── */

/**
 * GET /waap/domains/:anchorId/websites — account-level WAAP website list.
 *
 * Returns bare array (WAAP is NOT enveloped). Returns [] on 400/404 (no sites).
 */
export function useActAsWAAPWebsites(
  anchorId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: actAsWAAPKeys.websites(anchorId ?? ''),
    enabled: Boolean(anchorId) && enabled,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<ActAsWAAPWebsite[]> => {
      try {
        const res = await actAsClient.get<ActAsWAAPWebsite[] | null>(
          `/waap/domains/${anchorId}/websites`,
        );
        return res.data ?? [];
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return [];
        throw e;
      }
    },
  });
}

/**
 * GET /waap/domains/:domainId/website-info — read-only domain + CNAME info.
 * Returns null on 400/404 (not yet configured).
 */
export function useActAsWAAPWebsiteInfo(
  anchorId: string | undefined,
  domainId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', `website-info/${domainId ?? ''}`),
    enabled: Boolean(anchorId) && Boolean(domainId) && enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<ActAsWAAPWebsiteInfo | null> => {
      try {
        const res = await actAsClient.get<ActAsWAAPWebsiteInfo>(
          `/waap/domains/${domainId}/website-info`,
        );
        return res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

/* ── Analytics chart area constants ─────────────────────────────────────── */

/**
 * Path segments for the 7 analytics boards under
 * /waap/domains/:anchorId/<area>. Values are the verbatim URL fragment sent
 * to the backend and used as the react-query key discriminator.
 */
const ANALYTICS_AREAS = {
  monitoringCharts: 'analytics/monitoring/charts',
  deliveryCharts: 'analytics/delivery-overview/charts',
  securityCharts: 'analytics/security-overview/charts',
  aiWafCharts: 'analytics/ai-waf/charts',
  botCharts: 'analytics/bot-management/charts',
  apiCharts: 'analytics/api/charts',
  threatCharts: 'analytics/threat-investigation/charts',
  threatFilterOptions: 'analytics/threat-investigation/filter-options',
  threatAccessLogDetail: 'analytics/threat-investigation/access-log/detail',
} as const;

/* ── Analytics time-range constants (must match verbatim what the BE passes to
      Superset; changing a string breaks the Superset dataset filter) ────── */

export const MONITORING_TIME_RANGES = [
  'Last Hour',
  'Last 4 Hours',
  'Last 12 Hours',
  'Last 24 Hours',
] as const;
export type MonitoringTimeRange = (typeof MONITORING_TIME_RANGES)[number];

export const DELIVERY_TIME_RANGES = [
  'Last 1 hour',
  'Last 4 hours',
  'Last 12 hours',
  'Last 24 hours',
  'Last 7 days',
  'Last 30 days',
] as const;
export type DeliveryTimeRange = (typeof DELIVERY_TIME_RANGES)[number];

export const SECURITY_TIME_RANGES = [
  'Last 1 hour',
  'Last 4 hours',
  'Last 12 hours',
  'Last 24 hours',
  'Last 7 days',
  'Last 30 days',
] as const;
export type SecurityTimeRange = (typeof SECURITY_TIME_RANGES)[number];

export const AIWAF_TIME_RANGES = [
  'Last Hour',
  'Last 24 Hours',
  'Last 7 Days',
  'Last 30 Days',
  'This Month',
  'Last Month',
] as const;
export type AiWafTimeRange = (typeof AIWAF_TIME_RANGES)[number];

export const BOT_TIME_RANGES = [
  'Last Hour',
  'Last 24 Hours',
  'Last 7 Days',
  'Last 30 Days',
  'This Month',
  'Last Month',
] as const;
export type BotTimeRange = (typeof BOT_TIME_RANGES)[number];

export const API_TIME_RANGES = [
  'Last Hour',
  'Last 24 Hours',
  'Last 7 Days',
  'Last 30 Days',
  'This Month',
  'Last Month',
] as const;
export type ApiTimeRange = (typeof API_TIME_RANGES)[number];

export const THREAT_TIME_RANGES = [
  'Last 1 hour',
  'Last 4 hours',
  'Last 12 hours',
  'Last 24 hours',
  'Last 7 days',
  'Last 30 days',
] as const;
export type ThreatTimeRange = (typeof THREAT_TIME_RANGES)[number];

/** Sidebar filter values for Threat Investigation: each access-log column maps
 *  to a list of selected values; response-time is `upstream_response_time_min`. */
export type WaapThreatFilters = Record<string, string | string[]>;

/** Distinct dropdown options for Threat Investigation sidebar (keyed by column). */
export type WaapThreatFilterOptions = Record<string, string[]>;

/** Access-log row identity — used to open the per-request detail modal. */
export interface WaapAccessLogArgs {
  requestUuid: string;
  hostname: string;
  timestampMs?: number;
}

/** Full per-request access-log detail (vendor flat object ~100 fields). */
export type WaapAccessLogDetail = Record<string, unknown>;

/* ── Helper: build analytics query params ────────────────────────────────── */

function analyticsParams(tz: string, range: string, domains: string[]): string {
  const p = new URLSearchParams({ tz, range });
  const domainsKey = [...domains].sort().join(',');
  if (domainsKey) p.set('domains', domainsKey);
  return p.toString();
}

function isNotConfigured(status: number | undefined): boolean {
  return status === 400 || status === 404 || status === 501;
}

/* ── Analytics query key extension ──────────────────────────────────────── */
// actAsWAAPKeys.aspect already covers analytics boards (area = path segment).

/* ── Analytics hooks ─────────────────────────────────────────────────────── */

/**
 * GET /waap/domains/:anchorId/analytics/monitoring/charts
 * Returns null when board not configured (400/404/501).
 */
export function useActAsWAAPMonitoringCharts(
  anchorId: string | undefined,
  range: MonitoringTimeRange = 'Last 12 Hours',
  domains: string[] = [],
  enabled = true,
) {
  const domainsKey = [...domains].sort().join(',');
  return useQuery({
    queryKey: [...actAsWAAPKeys.aspect(anchorId ?? '', ANALYTICS_AREAS.monitoringCharts), range, domainsKey] as const,
    enabled: Boolean(anchorId) && enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await actAsClient.get<WaapChartsResponse>(
          `/waap/domains/${anchorId}/${ANALYTICS_AREAS.monitoringCharts}?${analyticsParams(tz, range, domains)}`,
        );
        return res.data;
      } catch (e) {
        if (isNotConfigured((e as { response?: { status?: number } })?.response?.status)) return null;
        throw e;
      }
    },
  });
}

/**
 * GET /waap/domains/:anchorId/analytics/delivery-overview/charts
 * Returns null when board not configured (400/404/501).
 */
export function useActAsWAAPDeliveryCharts(
  anchorId: string | undefined,
  range: DeliveryTimeRange = 'Last 4 hours',
  domains: string[] = [],
  enabled = true,
) {
  const domainsKey = [...domains].sort().join(',');
  return useQuery({
    queryKey: [...actAsWAAPKeys.aspect(anchorId ?? '', ANALYTICS_AREAS.deliveryCharts), range, domainsKey] as const,
    enabled: Boolean(anchorId) && enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await actAsClient.get<WaapChartsResponse>(
          `/waap/domains/${anchorId}/${ANALYTICS_AREAS.deliveryCharts}?${analyticsParams(tz, range, domains)}`,
        );
        return res.data;
      } catch (e) {
        if (isNotConfigured((e as { response?: { status?: number } })?.response?.status)) return null;
        throw e;
      }
    },
  });
}

/**
 * GET /waap/domains/:anchorId/analytics/security-overview/charts
 * Returns null when board not configured (400/404/501).
 */
export function useActAsWAAPSecurityCharts(
  anchorId: string | undefined,
  range: SecurityTimeRange = 'Last 4 hours',
  domains: string[] = [],
  enabled = true,
) {
  const domainsKey = [...domains].sort().join(',');
  return useQuery({
    queryKey: [...actAsWAAPKeys.aspect(anchorId ?? '', ANALYTICS_AREAS.securityCharts), range, domainsKey] as const,
    enabled: Boolean(anchorId) && enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await actAsClient.get<WaapChartsResponse>(
          `/waap/domains/${anchorId}/${ANALYTICS_AREAS.securityCharts}?${analyticsParams(tz, range, domains)}`,
        );
        return res.data;
      } catch (e) {
        if (isNotConfigured((e as { response?: { status?: number } })?.response?.status)) return null;
        throw e;
      }
    },
  });
}

/**
 * GET /waap/domains/:anchorId/analytics/ai-waf/charts
 * Returns null when board not configured (400/404/501).
 */
export function useActAsWAAPAiWafCharts(
  anchorId: string | undefined,
  range: AiWafTimeRange = 'Last 24 Hours',
  domains: string[] = [],
  enabled = true,
) {
  const domainsKey = [...domains].sort().join(',');
  return useQuery({
    queryKey: [...actAsWAAPKeys.aspect(anchorId ?? '', ANALYTICS_AREAS.aiWafCharts), range, domainsKey] as const,
    enabled: Boolean(anchorId) && enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await actAsClient.get<WaapChartsResponse>(
          `/waap/domains/${anchorId}/${ANALYTICS_AREAS.aiWafCharts}?${analyticsParams(tz, range, domains)}`,
        );
        return res.data;
      } catch (e) {
        if (isNotConfigured((e as { response?: { status?: number } })?.response?.status)) return null;
        throw e;
      }
    },
  });
}

/**
 * GET /waap/domains/:anchorId/analytics/bot-management/charts
 * Returns null when board not configured (400/404/501).
 */
export function useActAsWAAPBotCharts(
  anchorId: string | undefined,
  range: BotTimeRange = 'Last 24 Hours',
  domains: string[] = [],
  enabled = true,
) {
  const domainsKey = [...domains].sort().join(',');
  return useQuery({
    queryKey: [...actAsWAAPKeys.aspect(anchorId ?? '', ANALYTICS_AREAS.botCharts), range, domainsKey] as const,
    enabled: Boolean(anchorId) && enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await actAsClient.get<WaapChartsResponse>(
          `/waap/domains/${anchorId}/${ANALYTICS_AREAS.botCharts}?${analyticsParams(tz, range, domains)}`,
        );
        return res.data;
      } catch (e) {
        if (isNotConfigured((e as { response?: { status?: number } })?.response?.status)) return null;
        throw e;
      }
    },
  });
}

/**
 * GET /waap/domains/:anchorId/analytics/api/charts
 * No domain filter (API board is account-wide only).
 * Returns null when board not configured (400/404/501).
 */
export function useActAsWAAPApiCharts(
  anchorId: string | undefined,
  range: ApiTimeRange = 'Last 24 Hours',
  enabled = true,
) {
  return useQuery({
    queryKey: [...actAsWAAPKeys.aspect(anchorId ?? '', ANALYTICS_AREAS.apiCharts), range] as const,
    enabled: Boolean(anchorId) && enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const p = new URLSearchParams({ tz, range });
        const res = await actAsClient.get<WaapChartsResponse>(
          `/waap/domains/${anchorId}/${ANALYTICS_AREAS.apiCharts}?${p.toString()}`,
        );
        return res.data;
      } catch (e) {
        if (isNotConfigured((e as { response?: { status?: number } })?.response?.status)) return null;
        throw e;
      }
    },
  });
}

function cleanThreatFilters(filters: WaapThreatFilters): WaapThreatFilters {
  const out: WaapThreatFilters = {};
  for (const [k, v] of Object.entries(filters)) {
    if (Array.isArray(v)) {
      const arr = v.filter((x) => x.trim() !== '');
      if (arr.length) out[k] = arr;
    } else if (v.trim() !== '') {
      out[k] = v.trim();
    }
  }
  return out;
}

/**
 * GET /waap/domains/:anchorId/analytics/threat-investigation/filter-options
 * Returns {} when board not configured.
 */
export function useActAsWAAPThreatFilterOptions(
  anchorId: string | undefined,
  range: ThreatTimeRange = 'Last 4 hours',
  domains: string[] = [],
  enabled = true,
) {
  const domainsKey = [...domains].sort().join(',');
  return useQuery({
    queryKey: [...actAsWAAPKeys.aspect(anchorId ?? '', ANALYTICS_AREAS.threatFilterOptions), range, domainsKey] as const,
    enabled: Boolean(anchorId) && enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapThreatFilterOptions> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await actAsClient.get<WaapThreatFilterOptions>(
          `/waap/domains/${anchorId}/${ANALYTICS_AREAS.threatFilterOptions}?${analyticsParams(tz, range, domains)}`,
        );
        return res.data ?? {};
      } catch {
        return {};
      }
    },
  });
}

/**
 * GET /waap/domains/:anchorId/analytics/threat-investigation/charts
 * Returns null when board not configured (400/404/501).
 */
export function useActAsWAAPThreatCharts(
  anchorId: string | undefined,
  range: ThreatTimeRange = 'Last 4 hours',
  domains: string[] = [],
  filters: WaapThreatFilters = {},
  enabled = true,
) {
  const domainsKey = [...domains].sort().join(',');
  const filtersKey = JSON.stringify(cleanThreatFilters(filters));
  return useQuery({
    queryKey: [...actAsWAAPKeys.aspect(anchorId ?? '', ANALYTICS_AREAS.threatCharts), range, domainsKey, filtersKey] as const,
    enabled: Boolean(anchorId) && enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const p = new URLSearchParams({ tz, range });
        if (domainsKey) p.set('domains', domainsKey);
        if (filtersKey !== '{}') p.set('filters', filtersKey);
        const res = await actAsClient.get<WaapChartsResponse>(
          `/waap/domains/${anchorId}/${ANALYTICS_AREAS.threatCharts}?${p.toString()}`,
        );
        return res.data;
      } catch (e) {
        if (isNotConfigured((e as { response?: { status?: number } })?.response?.status)) return null;
        throw e;
      }
    },
  });
}

/**
 * GET /waap/domains/:anchorId/analytics/threat-investigation/access-log/detail
 * Returns null when not found (400/404/501). Timeout extended — vendor ClickHouse
 * query can run 1-2 min on first call (cold WAAP SSO session warm-up).
 */
export function useActAsWAAPAccessLogDetail(
  anchorId: string | undefined,
  args: WaapAccessLogArgs | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      ...actAsWAAPKeys.aspect(anchorId ?? '', ANALYTICS_AREAS.threatAccessLogDetail),
      args?.requestUuid,
      args?.hostname,
      args?.timestampMs,
    ] as const,
    enabled: Boolean(anchorId) && !!args && enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<WaapAccessLogDetail | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const p = new URLSearchParams({ tz, request_uuid: args!.requestUuid });
        if (args!.hostname) p.set('hostname', args!.hostname);
        if (args!.timestampMs != null) p.set('timestamp_ms', String(args!.timestampMs));
        const res = await actAsClient.get<WaapAccessLogDetail>(
          `/waap/domains/${anchorId}/${ANALYTICS_AREAS.threatAccessLogDetail}?${p.toString()}`,
          { timeout: 150_000 },
        );
        return res.data;
      } catch (e) {
        if (isNotConfigured((e as { response?: { status?: number } })?.response?.status)) return null;
        throw e;
      }
    },
  });
}

/* ── Log archive ─────────────────────────────────────────────────────────── */

/**
 * GET /waap/domains/:anchorId/log-archive/configs — log archiving config list.
 * Returns [] on 400/404 (no configs).
 */
export function useActAsWAAPLogArchiveConfigs(
  anchorId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'log-archive/configs'),
    enabled: Boolean(anchorId) && enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<ActAsWAAPLogArchiveConfig[]> => {
      try {
        const res = await actAsClient.get<ActAsWAAPLogArchiveConfig[] | null>(
          `/waap/domains/${anchorId}/log-archive/configs`,
        );
        return res.data ?? [];
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return [];
        throw e;
      }
    },
  });
}

/**
 * GET /waap/domains/:anchorId/log-archive/buckets — OSS bucket options.
 * Returns [] on 400/404.
 */
export function useActAsWAAPLogArchiveBuckets(
  anchorId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'log-archive/buckets'),
    enabled: Boolean(anchorId) && enabled,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<ActAsWAAPOSSBucket[]> => {
      try {
        const res = await actAsClient.get<ActAsWAAPOSSBucket[] | null>(
          `/waap/domains/${anchorId}/log-archive/buckets`,
        );
        return res.data ?? [];
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return [];
        throw e;
      }
    },
  });
}
